/*
    Меню-переключатель для компонента демо-формы

    Выводит список кнопок в многострочном режиме (десктоп)
    или в одну строку с горизонтальным скроллом (мобилка).

    В мобильной версии при клике на кнопку скроллит контейнер так,
    чтобы выбранная кнопка оказалась посередине.

    Обрабатывает mousemove для моб.разрешения экрана
    (горизонтальная прокрутка для устройств без поддержки свайпа).

    Может выводить пункты, скрытые "под катом" (hiddenOptions с hiddenOptionsLabel)

    Работает с hash (при useHash = true):
        - при переключении опции - добавляет ее id в hash
        - при первой загрузке - берет из хэша id опции
        и прокручивает страницу к родительской section

    Генерирует события:
        - update:modelValue - при обновлении активной опции
            (как при клике на кнопку, так и при использовании hash)
        - option-click - только при клике на кнопку
            (нужно для того, чтобы была возможность разграничивать обработку
            при клике и при использовании hash,
            например, фокусировать input демо-формы только при клике)
*/

import { nextTick } from "vue";
import dom from "../../dom";
import demoButton from "./demo-form-button";

const template = /* html */`
    <div class="nx-demo-form-menu-container">
        <div
            class="nx-demo-form-menu flex gap-col-0_125 gap-row-0_25 wrap-sm"
            ref="menuRef"
        >
            <span class="nx-demo-form-menu-fake hidden-desktop" />
            <demo-button
                v-for="option in options"
                :key="option.id"
                class="flex-0-auto flex items-center gap-0_25"
                :active="option.id === activeID"
                @click="handleClick(option.id, $event.target)"
                :data-test="option.id"
                :data-active="option.id === activeID"
            >
                [[ option.text ]]
                <div class="flex-0-auto flex gap-0_25" v-if="option.icons">
                    <img
                        v-for="icon in option.icons"
                        :key="icon"
                        :src="icon"
                        :alt="option.text"
                        class="nx-demo-form-menu-icon"
                    >
                </div>
            </demo-button>
            <demo-button
                v-if="hiddenOptionsLabel"
                :class="[
                    'flex-0 nx-hidden-options-toggler flex items-center gap-0_25 hidden-mobile',
                    hiddenOptionsVisible ? 'nx-hidden-options-toggler--active' : '',
                    (hiddenOptionActive && !hiddenOptionsVisible)
                        ? 'nx-hidden-options-toggler--badged'
                        : ''
                ]"
                @click="hiddenOptionsVisible = !hiddenOptionsVisible"
                transparent
                :hoverable="false"
                data-test="options-toggler"
            >
                [[ hiddenOptionsLabel ]]
                <i class="fa fa-chevron-down" />
            </demo-button>
            <template v-if="hiddenOptions">
                <transition name="slide-in">
                    <div
                        class="flex gap-col-0_125 gap-row-0_25 wrap-sm active-leave medium"
                        v-show="hiddenOptions && (hiddenOptionsVisible || !isDesktop)"
                    >
                        <demo-button
                            v-for="option in hiddenOptions"
                            :key="option.id"
                            class="flex-0"
                            :active="option.id === activeID"
                            @click="handleClick(option.id, $event.target)"
                            :data-test="option.id"
                            :data-active="option.id === activeID"
                        >
                            [[ option.text ]]
                        </demo-button>
                    </div>
                </transition>
            </template>
            <span class="nx-demo-form-menu-fake hidden-desktop" />
        </div>
    </div>
`;

const props = {
    modelValue: {
        type: String,
        default: "",
    },
    options: {
        type: Array,
        required: true,
    },
    hiddenOptions: {
        type: [Array, null],
        default: null,
    },
    hiddenOptionsLabel: {
        type: [String, null],
        default: null,
    },
    useHash: {
        type: Boolean,
        default: true,
    },
};

export default {
    template,
    props,
    data() {
        return {
            isDesktop: true,
            hiddenOptionsVisible: false,
            mql: null,
        };
    },
    emits: ["update:modelValue", "option-click"],
    computed: {
        activeID() {
            if (this.modelValue) return this.modelValue;
            return this.options[0].id;
        },
        hiddenOptionActive() {
            if (!this.hiddenOptions) return false;
            return (this.hiddenOptions.findIndex(option => option.id === this.activeID) !== -1);
        },
    },
    methods: {
        handleMouseMove(e) {
            // кастомный горизонтальный скролл для мобильного разрешения без поддержки свайпа
            this.$refs.menuRef.classList.add("no-events");
            this.$refs.menuRef.scrollLeft -= e.movementX;
        },
        handleMouseUp() {
            this.$refs.menuRef.classList.remove("no-events");
            this.$refs.menuRef.removeEventListener("mousemove", this.handleMouseMove);
            document.removeEventListener("mouseup", this.handleMouseUp);
        },
        scrollMenu(elem) {
            const activeItem = elem || this.$refs.menuRef.querySelector("[data-active='true']");
            if (!activeItem) {
                return;
            }
            // центрирует активную опцию для мобильных устройств
            // не используется scrollIntoView (так как скролл прерывается при фокусировке инпута)
            const menuBox = this.$refs.menuRef.getBoundingClientRect();
            const itemBox = activeItem.getBoundingClientRect();

            const menuCenter = menuBox.width / 2;
            const itemCenter = itemBox.width / 2;

            const scroll = (itemBox.left - menuCenter + itemCenter);
            if (!scroll) return;

            this.$refs.menuRef.scroll({
                top: 0,
                left: this.$refs.menuRef.scrollLeft + scroll,
                behavior: "smooth",
            });
        },
        handleClick(id, elem) {
            if (id === this.activeID) return;

            this.$emit("update:modelValue", id);
            this.$emit("option-click");

            if (this.useHash) {
                // добавляет в hash id кликнутой опции
                const href = window.location.origin + window.location.pathname;
                window.history.replaceState({}, "", `${href}#${id}`);
            }

            if (this.isDesktop) return;
            this.scrollMenu(elem);
        },
        handleHash() {
            // находит id активной опции в hash, обновляет modelValue и скроллит страницу
            const id = this.getIdFromHash();

            if (id) {
                this.$emit("update:modelValue", id);
                dom.callAfterLoaded(() => {
                    window.scrollTo({
                        top: this.$el.closest("section").offsetTop,
                        behavior: "smooth",
                    });
                });

                nextTick(() => {
                    if (this.hiddenOptionActive) {
                        this.hiddenOptionsVisible = true;
                    }
                    this.scrollMenu();
                });
            }
        },
        getIdFromHash() {
            const id = window.location.hash?.slice(1);
            const allOptions = [...this.options, ...(this.hiddenOptions || [])];
            const idInOptions = allOptions.findIndex(o => o.id === id);
            return idInOptions !== -1 ? id : null;
        },
        handleMqlChange(e) {
            this.isDesktop = e.matches;
            setTimeout(() => {
                this.scrollMenu();
            }, 100);
        },

    },
    mounted() {
        this.mql = window.matchMedia("(min-width: 972px)");
        this.isDesktop = this.mql.matches;

        this.mql.addEventListener("change", this.handleMqlChange);

        this.$refs.menuRef.addEventListener("mousedown", () => {
            if (this.isDesktop) return;
            this.$refs.menuRef.addEventListener("mousemove", this.handleMouseMove);
            document.addEventListener("mouseup", this.handleMouseUp);
        });

        if (this.useHash) {
            this.handleHash();
        }
    },
    beforeUnmount() {
        this.mql.removeEventListener("change", this.handleMqlChange);
    },
    components: { demoButton },
};
